// ./src/utils/misc.ts
import dayjs from "dayjs";

export const cloneDeep = function (obj: any): any {
  return JSON.parse(JSON.stringify(obj));
};

export const parseFlexibleDate = (
  date: string | number | Date | null | undefined,
): dayjs.Dayjs | null | undefined => {
  if (date == null) {
    return date;
  }

  if (date instanceof Date) {
    return dayjs(date);
  }

  if (typeof date === "string" && /^\d+$/.test(date)) {
    return dayjs(parseInt(date, 10));
  }

  return dayjs(date);
};

/**
 * Type for a debounced function with cancel method
 */
export type DebouncedFunction<T extends (...args: any[]) => any> = T & {
  cancel: () => void;
};

/**
 * Simple debounce implementation
 * Delays function execution until after a specified wait time has passed
 * since the last time it was invoked
 *
 * @param func - The function to debounce
 * @param wait - The number of milliseconds to delay
 * @returns Debounced function with cancel method
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number,
): DebouncedFunction<T> {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const debounced = (...args: Parameters<T>) => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      func(...args);
    }, wait);
  };

  debounced.cancel = () => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  return debounced as DebouncedFunction<T>;
}

// ./src/utils/misc.ts

/**
 * Safely stringify an object to JSON string
 * Returns stringified default object if serialization fails
 */
export const stringifyObject = (config: any, defaultObj: any = {}): string => {
  try {
    return JSON.stringify(config);
  } catch (error) {
    return JSON.stringify(defaultObj);
  }
};

/**
 * Safely parse a JSON string to an object
 * - Returns default object if parsing fails
 * - Keeps only properties that exist in the default config
 * - Adds missing properties from the default config
 * - Handles nested objects and arrays appropriately
 */
export const parseObject = <T extends Record<string, any>>(
  jsonString: string | null | undefined,
  defaultObj: T,
): T => {
  if (!jsonString) {
    return { ...defaultObj };
  }

  try {
    const parsed = JSON.parse(jsonString);

    // If parsed is not an object, return default
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return { ...defaultObj };
    }

    // Build result with only properties from default, using parsed values where valid
    const result: Record<string, any> = {};

    for (const key of Object.keys(defaultObj)) {
      const defaultValue = defaultObj[key];
      const parsedValue = parsed[key];

      // If parsed value is undefined, use default
      if (parsedValue === undefined) {
        result[key] = defaultValue;
        continue;
      }

      // Type checking based on default value type
      const defaultType = Array.isArray(defaultValue)
        ? "array"
        : typeof defaultValue;
      const parsedType = Array.isArray(parsedValue)
        ? "array"
        : typeof parsedValue;

      // If types match (or default is null which accepts any), use parsed value
      if (defaultType === parsedType || defaultValue === null) {
        result[key] = parsedValue;
      } else {
        // Type mismatch - use default
        result[key] = defaultValue;
      }
    }

    return result as T;
  } catch (error) {
    console.warn("Failed to parse object:", error);
    return { ...defaultObj };
  }
};

/**
 * Normalizes `result.data` from an API response into a typed array.
 *
 * Although OpResult always stores data as an array internally, the value
 * ultimately originates from a network response that we do not control.
 * A bug in the server or a schema mismatch can yield null, undefined, or a
 * plain object. This function handles every case defensively:
 *
 *  - null / undefined  → []  (no log — a legitimately empty result)
 *  - valid array       → cast and return as-is
 *  - plain object      → wrap in []  + console.warn so the bug is visible
 *  - anything else     → []  + console.warn
 *
 * Using console.warn (not console.error) because this is a data-shape issue
 * that doesn't break the user flow — the hook recovers gracefully — but it
 * absolutely needs to be visible to developers during debugging.
 */
export const normalizeData = <T>(data: unknown, context: string): T[] => {
  if (data == null) {
    return [];
  }

  if (Array.isArray(data)) {
    return data as T[];
  }

  if (typeof data === "object") {
    console.warn(
      `[useWorkflows] ${context}: expected array but received a plain object. ` +
        "The API may have a bug. Wrapping in array and continuing.",
      data,
    );
    return [data as T];
  }

  console.warn(
    `[useWorkflows] ${context}: expected array but received an unexpected value ` +
      `(type: ${typeof data}). Ignoring.`,
    data,
  );
  return [];
};
